import { Dialog } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import { getNames } from "country-list";
import { Box, HStack, VStack } from "../../components/utils";
import {
  Input,
  Label,
  CheckBoxWithLabel,
} from "../../components/shared/InputField";
import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import {
  useEditCaptable,
  useOnboardStage3Company,
} from "../../queries/newCapTable/companyOnboarding";
import {
  useAccountUsers,
  useDeleteCompanyUser,
} from "../../queries/companyUsers";
import Tooltip from "../../components/shared/Tooltip";
import {
  getCurrencyForCountry,
  inputMatchingSelectStyles,
} from "./OnboardCompanyUploadCard";

type Props = {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  company?: {
    uuid: string;
    nameOfTheCompany: string;
    cin?: string;
    jurisdiction?: string;
    valuation?: number;
    valuationDate?: string;
    sharePrice?: number;
    companyCurrency?: string;
    users?: Array<{
      id?: string | number;
      userId?: number;
      emailId?: string;
      email?: string;
    }>;
  } | null;
};

// Native date inputs require exactly YYYY-MM-DD, while the API may return an
// ISO timestamp (or another date-like value).
function formatDateForInput(value?: string): string {
  if (!value) return "";
  const stringValue = String(value).trim();
  // A date without a time must remain exactly as stored. Parsing it as a UTC
  // timestamp shifts the visible calendar day for some timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) return stringValue;
  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

const countries = getNames().sort();

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Company name is required"),
  cin: Yup.string().trim().required("CIN is required"),
  jurisdiction: Yup.string().trim().required("Jurisdiction is required"),
  valuation: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || Number(originalValue) === 0 ? null : value
    )
    .typeError("Valuation must be a number")
    .positive()
    .nullable(),
  valuationDate: Yup.string().nullable(),
  sharePrice: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || Number(originalValue) === 0 ? null : value
    )
    .typeError("Share price must be a number")
    .positive()
    .nullable(),
});

export const formatWithCommas = (value: string): string => {
  if (!value) return "";
  const [integerPart, decimalPart] = value.split(".");
  const formattedInteger = Number(integerPart).toLocaleString("en-IN");
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

export const formatSharePrice = (value: string): string => {
  if (!value || Number.isNaN(Number(value))) return "";
  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function AddCompanyModal({
  open,
  onClose,
  onDone,
  company,
}: Props) {
  const { mutate: onboardCompany, isLoading } = useOnboardStage3Company();
  const { mutate: editCaptable, isLoading: isEditing } = useEditCaptable();
  const { mutateAsync: deleteCompanyUser, isLoading: isRemovingUsers } =
    useDeleteCompanyUser();
  const { data: accountUsers } = useAccountUsers();
  const isEditMode = Boolean(company);
  const accountUserEmails = Array.from(
    new Set(
      (accountUsers || [])
        .map((u: any) => (u?.emailId || u?.email || "").trim().toLowerCase())
        .filter(Boolean)
    )
  );
  const companyUserEmails = new Set(
    (company?.users || [])
      .map((u: any) => (u?.emailId || u?.email || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const hasAllAccountUsers =
    accountUserEmails.length > 0 &&
    accountUserEmails.every((email) => companyUserEmails.has(email));

  const accountUsersWithAccess = (company?.users || []).filter((user: any) => {
    const email = (user?.emailId || user?.email || "").trim().toLowerCase();
    return (
      accountUserEmails.includes(email) &&
      Number.isFinite(Number(user?.userId ?? user?.id))
    );
  });

  const formik = useFormik({
    initialValues: {
      name: company?.nameOfTheCompany || "",
      cin: company?.cin || "",
      jurisdiction: company?.jurisdiction || "India",
      valuation:
        company?.valuation && company.valuation > 0
          ? company.valuation.toString()
          : "",
      valuationDate: formatDateForInput(company?.valuationDate),
      sharePrice:
        company?.sharePrice && company.sharePrice > 0
          ? company.sharePrice.toFixed(2)
          : "",
      addAllUsers: hasAllAccountUsers,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const emails = values.addAllUsers
        ? Array.from(
            new Set(
              (accountUsers || [])
                .map((u: any) => (u?.emailId || u?.email || "").trim())
                .filter(Boolean)
            )
          )
        : undefined;

      const currencyEntry = getCurrencyForCountry(values.jurisdiction);

      if (company) {
        editCaptable(
          {
            companyId: company.uuid,
            name: values.name.trim(),
            cin: values.cin.trim(),
            jurisdiction: values.jurisdiction.trim(),
            companyCurrency:
              currencyEntry?.split(" - ")[0] || company.companyCurrency,
            ...(Number(values.valuation) > 0
              ? { valuation: Number(values.valuation) }
              : {}),
            ...(Number(values.sharePrice) > 0
              ? { sharePrice: Number(values.sharePrice) }
              : {}),
            ...(values.valuationDate
              ? { valuationDate: values.valuationDate }
              : {}),
            ...(emails ? { emails } : {}),
          },
          {
            onSuccess: async () => {
              if (!values.addAllUsers && hasAllAccountUsers) {
                try {
                  await Promise.all(
                    accountUsersWithAccess.map((user: any) =>
                      deleteCompanyUser({
                        companyId: company.uuid,
                        userId: Number(user.userId ?? user.id),
                      })
                    )
                  );
                } catch (err: any) {
                  toast(
                    err?.response?.data?.errorMessage ||
                      "Company details were updated, but some user access could not be removed.",
                    { type: "error", autoClose: 3000 }
                  );
                  return;
                }
              }
              toast(`Updated company "${values.name.trim()}"`, {
                type: "success",
                autoClose: 2000,
              });
              resetForm();
              onDone?.();
              onClose();
            },
            onError: (err: any) => {
              toast(
                err?.response?.data?.errorMessage || "Failed to update company",
                { type: "error", autoClose: 3000 }
              );
            },
          }
        );
        return;
      }

      onboardCompany(
        {
          name: values.name.trim(),
          cin: values.cin.trim(),
          jurisdiction: values.jurisdiction.trim(),
          companyCurrency: currencyEntry
            ? currencyEntry.split(" - ")[0]
            : undefined,
          valuation: values.valuation ? Number(values.valuation) : undefined,
          valuationDate: values.valuationDate || undefined,
          sharePrice: values.sharePrice ? Number(values.sharePrice) : undefined,
          emails,
          isTaskManagerCompany: true,
          isPrivate: true,
        },
        {
          onSuccess: () => {
            toast(`Onboarded company "${values.name.trim()}"`, {
              type: "success",
              autoClose: 2000,
            });
            resetForm();
            onDone?.();
            onClose();
          },
          onError: (err: any) => {
            toast(
              err?.response?.data?.errorMessage || "Failed to add company",
              { type: "error", autoClose: 3000 }
            );
          },
        }
      );
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box className="p-5">
        <HStack className="items-center justify-between pb-4">
          <p className="text-base font-semibold text-stone-800">
            {isEditMode ? "Edit Company" : "Add Company"}
          </p>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer text-stone-500 hover:text-stone-700"
            onClick={handleClose}
          />
        </HStack>

        <VStack className="gap-3">
          <HStack className="gap-3">
            <VStack className="flex-1">
              <Label>
                Company Name<span className="text-rose-400">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter company name"
                {...formik.getFieldProps("name")}
              />
              {formik.touched.name && formik.errors.name && (
                <span className="text-xxs text-red-500">
                  {formik.errors.name}
                </span>
              )}
            </VStack>

            <VStack className="flex-1">
              <Label>
                CIN<span className="text-rose-400">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter CIN"
                {...formik.getFieldProps("cin")}
              />
              {formik.touched.cin && formik.errors.cin && (
                <span className="text-xxs text-red-500">
                  {formik.errors.cin}
                </span>
              )}
            </VStack>
          </HStack>

          <HStack className="gap-3">
            <VStack className="flex-1">
              <Label>
                Jurisdiction<span className="text-rose-400">*</span>
              </Label>
              <Select
                options={countries.map((country) => ({
                  label: country,
                  value: country,
                }))}
                value={
                  formik.values.jurisdiction
                    ? {
                        label: formik.values.jurisdiction,
                        value: formik.values.jurisdiction,
                      }
                    : null
                }
                placeholder="Select jurisdiction"
                onChange={(option) =>
                  formik.setFieldValue("jurisdiction", option?.value || "")
                }
                onBlur={() => formik.setFieldTouched("jurisdiction", true)}
                isClearable
                classNamePrefix="select2-selection"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  ...inputMatchingSelectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1400 }),
                }}
              />
              {formik.touched.jurisdiction && formik.errors.jurisdiction && (
                <span className="text-xxs text-red-500">
                  {formik.errors.jurisdiction}
                </span>
              )}
            </VStack>

            <VStack className="flex-1">
              <Label>Valuation (optional)</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="Enter valuation"
                name="valuation"
                value={formatWithCommas(formik.values.valuation)}
                onChange={(e) =>
                  formik.setFieldValue(
                    "valuation",
                    e.target.value.replace(/[^0-9.]/g, "")
                  )
                }
                onBlur={formik.handleBlur}
              />
              {formik.touched.valuation && formik.errors.valuation && (
                <span className="text-xxs text-red-500">
                  {formik.errors.valuation}
                </span>
              )}
            </VStack>
          </HStack>

          <HStack className="gap-3">
            <VStack className="flex-1">
              <Label>Valuation Date (optional)</Label>
              <Input
                type="date"
                placeholder="Eg:DD/MM/YYYY"
                {...formik.getFieldProps("valuationDate")}
              />
              {formik.touched.valuationDate && formik.errors.valuationDate && (
                <span className="text-xxs text-red-500">
                  {formik.errors.valuationDate}
                </span>
              )}
            </VStack>

            <VStack className="flex-1">
              <Label>Share Price (optional)</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="Enter share price"
                name="sharePrice"
                value={formik.values.sharePrice}
                onChange={(e) => {
                  const nextValue = e.target.value.replace(/[^0-9.]/g, "");
                  const [whole = "", decimal = ""] = nextValue.split(".");
                  formik.setFieldValue(
                    "sharePrice",
                    nextValue.includes(".")
                      ? `${whole}.${decimal.slice(0, 2)}`
                      : whole
                  );
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  if (formik.values.sharePrice) {
                    formik.setFieldValue(
                      "sharePrice",
                      Number(formik.values.sharePrice).toFixed(2)
                    );
                  }
                }}
              />
              {formik.touched.sharePrice && formik.errors.sharePrice && (
                <span className="text-xxs text-red-500">
                  {formik.errors.sharePrice}
                </span>
              )}
            </VStack>
          </HStack>

          <HStack className="items-center mt-2 gap-2">
            <CheckBoxWithLabel
              id="addAllUsers"
              label="Add all account users to this company"
              labelclassname="text-sm text-stone-600 ml-2"
              checked={formik.values.addAllUsers}
              onChange={(e) =>
                formik.setFieldValue("addAllUsers", e.target.checked)
              }
            />
            <Tooltip
              _className={`w-36`}
              text={"Grants every user on your account access to this company"}
            >
              <Icon
                icon="material-symbols:info-outline"
                width={20}
                className="text-gray-400"
                height={20}
              />
            </Tooltip>
          </HStack>
        </VStack>

        <HStack className="justify-end gap-3 pt-5">
          <SecondaryCTAButton
            type="button"
            event-name={
              isEditMode ? "Cancel Edit Company" : "Cancel Add Company"
            }
            onClick={handleClose}
          >
            Cancel
          </SecondaryCTAButton>
          <PrimaryCTAButton
            type="button"
            event-name={isEditMode ? "Edit Company" : "Add Company"}
            loading={isLoading || isEditing || isRemovingUsers}
            onClick={() => formik.handleSubmit()}
          >
            {isEditMode ? "Edit" : "Add Company"}
          </PrimaryCTAButton>
        </HStack>
      </Box>
    </Dialog>
  );
}
